import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import EnumSelect from '../../Fundamentals/Form/EnumSelect';
import type { FormControlAriaProps } from '../../Fundamentals/Form/FormField';
import { VossTimeLengthUnits, VossTimeLengthUnitsText, timeLengthUnits } from '../../../Constants/VOSS/vossTimeLengthUnits';
import type { CycleLength } from './Packages.types';

interface TimeLengthFieldsProps<TValue extends CycleLength> extends FormControlAriaProps {
    id: string;
    value: TValue;
    onChange: (value: TValue) => void;
    // null means "no end date" in VOSS; clearing the number input produces it when allowed
    allowEmpty?: boolean;
    allowNegative?: boolean;
    disabled?: boolean;
}

const INTEGER = /^-?\d+$/;
const PARTIAL_INTEGER = /^-?\d*$/;

const toText = (value: CycleLength) => (value === null ? '' : String(value.value));

const TimeLengthFields = <TValue extends CycleLength>({
    id,
    value,
    onChange,
    allowEmpty = false,
    allowNegative = false,
    disabled = false,
    ...ariaProps
}: TimeLengthFieldsProps<TValue>) => {
    const { t } = useTranslation();
    // a text input keeps a half-typed "-" visible; a number input reports it as "" and the value would snap to 0
    const [editingText, setEditingText] = useState<string | null>(null);
    // keeps the unit while the number is cleared so retyping does not fall back to Month
    const [fallbackUnit, setFallbackUnit] = useState(value?.unit ?? VossTimeLengthUnits.Month);
    const unit = value?.unit ?? fallbackUnit;

    const onTextChange = (raw: string) => {
        if (!PARTIAL_INTEGER.test(raw) || (!allowNegative && raw.startsWith('-'))) return;
        setEditingText(raw);

        if (INTEGER.test(raw)) {
            onChange({ value: Number(raw), unit } as TValue);
        } else if (raw === '') {
            setFallbackUnit(unit);
            onChange((allowEmpty ? null : { value: 0, unit }) as TValue);
        }
    };

    return (
        <div className="d-flex gap-8">
            <input
                id={id}
                type="text"
                inputMode="numeric"
                className="form-control flex-1"
                disabled={disabled}
                placeholder={allowEmpty ? (t('packages.evergreen') ?? '') : undefined}
                value={editingText ?? toText(value)}
                onChange={(e) => onTextChange(e.target.value)}
                onBlur={() => setEditingText(null)}
                {...ariaProps}
            />
            <EnumSelect
                className="form-control flex-1"
                ariaLabel={t('packages.unit') ?? ''}
                disabled={disabled || value === null}
                value={unit}
                options={timeLengthUnits}
                getLabel={(option) => t(VossTimeLengthUnitsText[option])}
                onChange={(option) => onChange((value === null ? null : { ...value, unit: option }) as TValue)}
            />
        </div>
    );
};

export default TimeLengthFields;
